import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  FC,
  MouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";

import { DesignToken, RadiusToken } from "@livechat/design-system-react-components";

import { KeyCodeEnum } from "constants/keyCode";
import { withVar } from "themes/helpers";

import { dropdownListStyles } from "./styles";

export type CannedResponsesListItems = {
  itemId: string;
  content: ReactNode;
  onItemSelect: (
    itemId: string,
    event: MouseEvent<HTMLElement> | ReactKeyboardEvent<HTMLElement> | KeyboardEvent
  ) => void;
};

interface IProps {
  items: CannedResponsesListItems[];
}

export const CannedResponsesList: FC<IProps> = memo(({ items }) => {
  const [focusIndex, setFocusIndex] = useState<number>(0);

  const focusElement = useCallback(
    (value: number) => {
      const newIndex = focusIndex + value;
      const elementToScroll = document.getElementById(items[newIndex].itemId);
      setFocusIndex(newIndex);

      elementToScroll?.scrollIntoView({
        behavior: "smooth",
      });
    },
    [items, focusIndex]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === KeyCodeEnum.ArrowUp) {
        e.preventDefault();

        return focusIndex > 0 ? focusElement(-1) : focusElement(items.length - 1);
      }

      if (e.keyCode === KeyCodeEnum.ArrowDown) {
        e.preventDefault();

        return focusIndex + 1 < items.length ? focusElement(1) : focusElement(-items.length + 1);
      }

      if (e.keyCode === KeyCodeEnum.Enter || e.keyCode === KeyCodeEnum.ArrowRight) {
        e.preventDefault();
        items[focusIndex].onItemSelect(items[focusIndex].itemId, e);
      }
    },
    [items, focusIndex, focusElement]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return (): void => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <ListWrapper tabIndex={0}>
      {items.map(({ itemId, content, onItemSelect }, index) => (
        <ListItem
          tabIndex={-1}
          role="button"
          key={itemId}
          id={itemId}
          onClick={e => onItemSelect(itemId, e)}
          focused={focusIndex === index}
          onMouseEnter={(): void => setFocusIndex(index)}
        >
          {content}
        </ListItem>
      ))}
    </ListWrapper>
  );
});

CannedResponsesList.displayName = "CannedResponsesList";

const ListItem = styled.li<{ focused: boolean }>`
  border-radius: ${withVar(RadiusToken.Radius3)};

  ${({ focused }): SerializedStyles =>
    focused
      ? css`
          background-color: ${withVar(DesignToken.SurfacePrimaryHover)};
        `
      : css``}
`;

const ListWrapper = styled.ul`
  ${dropdownListStyles()}
  overflow-y: auto;
`;
